'use client';

import Image from 'next/image';

import { useEffect, useRef, useState, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Slider, { Settings } from 'react-slick';
import { Game, GamesFilter, GamesFilterKey, SportsSubcategory } from '@/types/Game';

interface Props {
  games: Game[];
  filters: GamesFilter[];
  selectedFilter: GamesFilterKey;
  selectedSportFilter: SportsSubcategory | null;
  filterAnimationDuration: number;
  onImagesLoaded: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getSliderSettings = (_gameCount: number): Settings => {
  return {
    speed: 400,
    swipeToSlide: false,
    variableWidth: true,
    touchThreshold: 15,
    infinite: true,
    arrows: false,
    centerMode: true,
    centerPadding: '0px',
    initialSlide: 0,
  };
};

// calculates distance from center slide when infinite:true
const getDistanceFromCenter = (current: number, center: number, total: number): number => {
  const diff = Math.abs(current - center);
  return Math.min(diff, total - diff);
};

// get slide opacity based on distance from the center slide
const getSlideOpacity = (distance: number, isSwiping: boolean): number => {
  if (isSwiping) {
    return distance === 0 ? 1 : 0.2;
  }

  switch (distance) {
    case 0:
      return 1;
    case 1:
      return 0.5;
    case 2:
      return 0.2;
    case 3:
      return 0.05;
    default:
      return 0.05;
  }
};

const FILTER_SHADOW_MAP: Record<GamesFilterKey, string> = {
  all: 'shadow-primary',
  sports: 'shadow-secondary',
  casino: 'shadow-pink',
  jackpots: 'shadow-primary',
};

// TODO: For performance optimization, prevent images reload on filter change
const GamesSwiper = ({
  games,
  selectedFilter,
  selectedSportFilter,
  filterAnimationDuration,
  onImagesLoaded,
}: Props) => {
  const [centerSlide, setCenterSlide] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const loadedImages = useRef<Set<string>>(new Set());
  const imagesLoadedFlag = useRef(false);
  const sliderRef = useRef<Slider>(null);

  // 1. Refactored Filtering Logic: Move out of the loop and use useMemo
  const filteredGames = useMemo(() => {
    let filtered = games;
    if (selectedFilter !== 'all') {
      filtered = games.filter(game => game.category === selectedFilter);
    }
    if (selectedFilter === 'sports' && selectedSportFilter) {
      filtered = filtered.filter(game => game.subcategory === selectedSportFilter);
    }
    return filtered;
  }, [games, selectedFilter, selectedSportFilter]);
  const gapSpace = filteredGames.length < 5 ? 150 : 170;
  // we want to show only odd number of games
  const gameCount = filteredGames.length % 2 === 0 ? filteredGames.length - 1 : filteredGames.length;
  const maxContainerWidth = Math.min(gameCount, 5) * 232 + gapSpace;
  // Reset center slide when filter changes
  useEffect(() => {
    setCenterSlide(0);
  }, [selectedFilter, selectedSportFilter]);

  const handleImageLoaded = (game: Game) => {
    if (imagesLoadedFlag.current) return;
    loadedImages.current.add(game.id);

    if (loadedImages.current.size === games.length) {
      imagesLoadedFlag.current = true;
      onImagesLoaded();
    }
  };

  const handleSlideClick = (index: number) => {
    if (sliderRef.current && index !== centerSlide) {
      sliderRef.current.slickGoTo(index);
    }
  };

  return (
    <motion.div
      className='w-full mx-auto overflow-hidden min-h-[500px]'
      // 2. Animate the maxWidth property
      animate={{ maxWidth: `${maxContainerWidth}px` }}
      // 3. Match the timing with your filterAnimationDuration
      transition={{ duration: filterAnimationDuration, ease: 'easeInOut' }}>
      <AnimatePresence mode='wait'>
        <motion.div
          // Unique key forces the animation to trigger on filter/category change
          key={`${selectedFilter}-${selectedSportFilter}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: filterAnimationDuration, ease: 'easeInOut' }}
          style={{ touchAction: 'pan-y' }}>
          <Slider
            ref={sliderRef}
            {...getSliderSettings(filteredGames.length)}
            key={`slider-${filteredGames.length}`} // Reset Slick track for new data
            beforeChange={(_, next) => setCenterSlide(next)}
            className='w-full h-[560px]'
            onSwipe={() => setIsSwiping(true)} // Improved swipe detection
            afterChange={() => setIsSwiping(false)}>
            {filteredGames.map((game, index) => {
              const distance = getDistanceFromCenter(index, centerSlide, filteredGames.length);
              const opacity = getSlideOpacity(distance, isSwiping);
              const isCenterSlide = distance === 0;

              return (
                <motion.div
                  key={game.id}
                  className='relative w-full h-[550px] px-5 sm:px-2 py-10 focus:outline-none'
                  initial={{ opacity: 0 }}
                  animate={{ opacity }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: filterAnimationDuration, ease: 'easeInOut' }}
                  onClick={() => handleSlideClick(index)}
                  style={{ cursor: 'pointer' }}>
                  <div className='relative w-[232px] h-[480px] mx-auto'>
                    {isCenterSlide && (
                      <motion.div
                        className={`absolute top-0 left-1/2 -translate-x-1/2 w-[232px] h-[480px] rounded-[40px] ${FILTER_SHADOW_MAP[selectedFilter]}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: isCenterSlide ? 1 : 0 }}
                        transition={{ duration: filterAnimationDuration, ease: 'easeInOut' }}
                      />
                    )}

                    <Image
                      priority
                      src={game.img}
                      alt={game.name}
                      className='w-full h-full rounded-[40px] relative z-2'
                      onLoad={() => handleImageLoaded(game)}
                    />
                  </div>
                </motion.div>
              );
            })}
          </Slider>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

export default GamesSwiper;
